import Loading from '../components/loading';
import ModalError from '../components/modal-error';
import { GetStatus, GetUser } from '../helper/local-store';
import {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GlobalContext } from './global-context';
import Modal from '../components/modal';
import axiosInstance from '../helper/axios-instance';
import * as faceapi from 'face-api.js';

type Props = {
  children: ReactNode;
};

export const GlobalProvider = ({ children }: Props) => {
  const navigation = useNavigate();
  const location = useLocation();

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const screenVideoRef = useRef<HTMLVideoElement>(null);
  const screenCanvasRef = useRef<HTMLCanvasElement>(null);
  const screenStreamRef = useRef<MediaStream | null>(null);

  const randomCaptureFlag = useRef(false);
  const screerCaptureFlag = useRef(false);
  const faceDetectionFlag = useRef(false);

  const randomCaptureInterval = useRef<NodeJS.Timeout | null>(null);
  const screenCaptureInterval = useRef<NodeJS.Timeout | null>(null);
  const windowOnBlurCallback = useRef<() => void>(() => {});

  const scrollRef = useRef<HTMLDivElement>(null);

  const [errorModal, setErrorModal] = useState(false);
  const [isNetWorkError, setIsNetWorkError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isNeedCamera, setIsNeedCamera] = useState(false);
  const [isNeedScreenShare, setIsNeedScreenShare] = useState(false);
  const [openTest, setOpenTest] = useState(false);

  const isEnglish = () => {
    try {
      const user = GetUser();
      if (user) {
        return user.language === 'english';
      }
    } catch {
      return false;
    }
    return false;
  };

  const startWebcam = async () => {
    if (streamRef.current) {
      return streamRef.current;
    }

    return navigator.mediaDevices
      .getUserMedia({ video: true })
      .then((stream) => {
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.autoplay = true;
        }

        const [track] = stream.getVideoTracks();
        track.addEventListener('ended', () => {
          setIsNeedCamera(true);
        });

        setIsNeedCamera(false);
        return stream;
      })
      .catch(() => {
        setIsNeedCamera(true);
        return null;
      });
  };

  const startScreenCapture = useCallback(async () => {
    if (screenStreamRef.current) {
      return screenStreamRef.current;
    }

    return navigator.mediaDevices
      .getDisplayMedia({
        video: {
          displaySurface: 'monitor',
        },
      })
      .then((stream) => {
        screenStreamRef.current = stream;
        if (screenVideoRef.current) {
          screenVideoRef.current.srcObject = stream;
          screenVideoRef.current.autoplay = true;
        }

        const [track] = stream.getVideoTracks();
        const settings = track.getSettings();

        if (settings.displaySurface !== 'monitor') {
          track.stop();
          setIsNeedScreenShare(true);
          return null;
        }

        track.addEventListener('ended', () => {
          setIsNeedScreenShare(true);
        });

        setIsNeedScreenShare(false);
        return stream;
      })
      .catch(() => {
        setIsNeedScreenShare(true);
        return null;
      });
  }, []);

  const captureImage = (
    type = 'capture',
    detectAllFaces: faceapi.FaceDetection[] = []
  ) => {
    if (!canvasRef.current || !videoRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;

    const size = 320;
    canvas.width = size;
    canvas.height = size;

    const scale = Math.min(size / video.videoWidth, size / video.videoHeight);
    const x = (size - video.videoWidth * scale) / 2;
    const y = (size - video.videoHeight * scale) / 2;

    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(
        video,
        0,
        0,
        video.videoWidth,
        video.videoHeight,
        x,
        y,
        video.videoWidth * scale,
        video.videoHeight * scale
      );

      const now = new Date();
      const dateString = now.toLocaleString();
      context.fillStyle = 'red';
      context.font = '12px Arial';

      const textWidth = context.measureText(dateString).width;
      const xText = canvas.width - textWidth - 10;
      const yText = canvas.height - 10;

      context.fillText(dateString, xText, yText);

      if (type === 'incident') {
        if (detectAllFaces.length > 0) {
          detectAllFaces.forEach((det) => {
            const { x, y, width, height } = det.box;

            context.strokeStyle = 'green';
            context.lineWidth = 2;
            context.strokeRect(
              x * scale,
              y * scale + (size - video.videoHeight * scale) / 2,
              width * scale,
              height * scale
            );
          });
        }

        const textNotif =
          detectAllFaces.length > 1
            ? 'Multiple faces detected'
            : 'No face detected';

        context.fillStyle = 'red';
        context.font = '12px Arial';

        const textWidth = context.measureText(textNotif).width;
        const xText = canvas.width - textWidth - 10;
        const yText = 10;
        context.fillText(textNotif, xText, yText);
      }
    }

    const dataImage = canvas.toDataURL('image/jpeg', 0.8);
    axiosInstance
      .post('/save-image', {
        type: type,
        image: dataImage,
        filename: `random-capture-${Date.now()}.png`,
      })
      .catch(() => {
        console.log('failed save random capture image');
      });
  };

  const captureScreen = (type = 'capture') => {
    if (!screenCanvasRef.current || !screenVideoRef.current) return;

    const canvas = screenCanvasRef.current;
    const video = screenVideoRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

      const now = new Date();
      const dateString = now.toLocaleString();
      context.fillStyle = 'red';
      context.font = '32px Arial';

      const textWidth = context.measureText(dateString).width;
      const x = canvas.width - textWidth - 10;
      const y = canvas.height - 10;

      context.fillText(dateString, x, y);
    }

    const dataImage = canvas.toDataURL('image/jpeg', 0.8);
    axiosInstance
      .post('/save-image-screen', {
        type: type,
        image: dataImage,
        filename: `random-screen-capture-${Date.now()}.png`,
      })
      .catch(() => {
        console.log('failed save random screen capture image');
      });
  };

  const showNetworkError = useCallback(() => {
    setIsNetWorkError(true);
    setErrorModal(true);
  }, []);

  useEffect(() => {
    setTimeout(() => setIsLoading(false), 500);
  }, []);

  useEffect(() => {
    if (isNeedCamera || isNeedScreenShare) return;

    const status = GetStatus();
    if (!status || !(status.random_capture || status.proctoring)) {
      return;
    }

    const path = location.pathname;
    const pathList = ['/', '/instruction', '/test'];

    axiosInstance.get('/ping').catch((error) => {
      if (error.code === 'ERR_NETWORK') {
        showNetworkError();
      }
    });

    if (path !== status.curr_path) {
      return;
    }

    if (!pathList.includes(path)) {
      return;
    }

    if (!randomCaptureFlag.current) {
      randomCaptureFlag.current = true;
      startWebcam().then(() => {
        randomCaptureInterval.current = setInterval(() => {
          captureImage();
        }, 30 * 1000);

        if (status.proctoring) {
          let detectedFace = 0;
          let timeDetectedFace = 0;
          let startTimeIncidentDetectedFace = 0;
          let endTimeIncidentDetectedFace = 0;

          faceapi.nets.tinyFaceDetector.loadFromUri('/models').then(() => {
            const faceDetection = async () => {
              if (!faceDetectionFlag.current) return;

              const detections = await faceapi.detectAllFaces(
                videoRef.current as HTMLVideoElement,
                new faceapi.TinyFaceDetectorOptions()
              );
              if (detections.length !== detectedFace) {
                timeDetectedFace++;
                if (timeDetectedFace > 20) {
                  detectedFace = detections.length;
                  if (detectedFace !== 1) {
                    startTimeIncidentDetectedFace = Date.now();
                    captureImage('incident', detections);
                  }
                }
              } else {
                timeDetectedFace = 0;
                if (detectedFace !== 1) {
                  endTimeIncidentDetectedFace = Date.now();
                  const timeDiff =
                    (endTimeIncidentDetectedFace -
                      startTimeIncidentDetectedFace) /
                    1000;
                  if (timeDiff > 30) {
                    startTimeIncidentDetectedFace = Date.now();
                    captureImage('incident', detections);
                  }
                }
              }
              setTimeout(() => {
                faceDetection();
              }, 0);
            };

            faceDetectionFlag.current = true;
            faceDetection();
          });
        }
      });
    }

    if (status.proctoring && !screerCaptureFlag.current) {
      screerCaptureFlag.current = true;
      startScreenCapture().then(() => {
        screenCaptureInterval.current = setInterval(() => {
          captureScreen();
        }, 30 * 1000);

        windowOnBlurCallback.current = () => {
          setTimeout(() => {
            captureScreen('incident');
          }, 1000);
        };
        window.addEventListener('blur', windowOnBlurCallback.current);
      });
    }
  }, [
    location.pathname,
    isNeedCamera,
    isNeedScreenShare,
    startScreenCapture,
    showNetworkError,
  ]);

  useEffect(() => {
    return () => {
      faceDetectionFlag.current = false;
      if (randomCaptureInterval.current) {
        clearInterval(randomCaptureInterval.current);
      }
      if (screenCaptureInterval.current) {
        clearInterval(screenCaptureInterval.current);
      }
      window.removeEventListener('blur', windowOnBlurCallback.current);
    };
  }, []);

  const showServerError = useCallback(() => {
    setIsNetWorkError(false);
    setErrorModal(true);
  }, []);

  const navigate = useCallback(
    (path: string) => {
      navigation(path);
    },
    [navigation]
  );

  const handleScrollUp = useCallback(() => {
    scrollRef.current?.scrollIntoView({
      behavior: 'smooth',
    });
  }, []);

  if (isNeedCamera) {
    return (
      <Modal
        size="sm"
        modalState={isNeedCamera}
        closeColor="primary"
        closeText="Ok"
        handleCloseModal={() => {
          window.location.reload();
        }}
      >
        <div className="text-center">
          {isEnglish()
            ? 'Please enable your camera to continue the assessment.'
            : 'Mohon untuk menyalakan camera Anda agar dapat melanjutkan asesmen.'}
        </div>
      </Modal>
    );
  }

  if (isNeedScreenShare) {
    return (
      <Modal
        size="sm"
        modalState={isNeedScreenShare}
        closeColor="primary"
        closeText="Ok"
        handleCloseModal={() => {
          window.location.reload();
        }}
      >
        <div className="text-center">
          {isEnglish()
            ? 'Please enable your screen share and select "entire screen" to continue the assessment.'
            : 'Mohon untuk menyalakan screen share Anda dan pilih "entire screen" agar dapat melanjutkan asesmen.'}
        </div>
      </Modal>
    );
  }

  if (isNetWorkError) {
    return <ModalError state={errorModal} isNetWorkError={isNetWorkError} />;
  }

  return (
    <GlobalContext.Provider
      value={{
        showNetworkError,
        showServerError,
        navigate,
        startWebcam,
        startScreenCapture,
        handleScrollUp,
        openTest,
        setOpenTest,
      }}
    >
      <div ref={scrollRef}>
        {isLoading ? <Loading /> : children}
        <video ref={videoRef} className="hidden" />
        <canvas ref={canvasRef} className="hidden"></canvas>
        <video ref={screenVideoRef} className="hidden" />
        <canvas ref={screenCanvasRef} className="hidden"></canvas>
      </div>
    </GlobalContext.Provider>
  );
};
